import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { fetchCrmInstitutesById } from '../../../services/crmInstituteService';
// import { ArrowLeft } from "react-feather";

interface Institute {
  crm_institute_id: number;
  institute_name: string;
  status: string;
  assigned_to: number;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
  };
  contacts: Contact[];
  address: Address[];
}

interface Contact {
  contact_id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
}
interface Address {
  address_id: number;
  address_type: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  CDC_FLAG: string;
}

const InstituteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [institute, setInstitute] = useState<Institute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadInstituteDetails = async () => {
      try {
        const instituteData = await fetchCrmInstitutesById(id);
        setInstitute(instituteData);
      } catch (err) {
        setError('Failed to fetch institute details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadInstituteDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
        <p className="ms-2">Loading Institute Details...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="container mt-4">
      {/* Institute Details Card */}
      <Card className="shadow border-0 rounded-3 p-4">
        <Card.Body>
          <Card.Title className="fs-3 fw-bold">
            {institute?.institute_name}
          </Card.Title>
          <Card.Subtitle className="text-muted">
            Status: {institute?.status}
          </Card.Subtitle>
          <hr />
          <p className="mb-2">
            <strong>Assigned To:</strong> {institute?.user.username}
          </p>
          <p className="mb-2">
            <strong>Created At:</strong>{' '}
            {new Date(institute?.created_at || '').toLocaleString()}
          </p>
          <p className="mb-2">
            <strong>Updated At:</strong>{' '}
            {new Date(institute?.updated_at || '').toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      {/* Contacts Section */}
      <div className="mt-5">
        <h3 className="fw-bold"> Contacts Details</h3>
        {institute?.contacts && institute.contacts.length > 0 ? (
          <div className="row g-4">
            {institute.contacts.map(contact => (
              <div key={contact.contact_id} className="col-md-6 col-lg-4">
                <Card className="border-0 shadow-sm p-3">
                  <Card.Body>
                    <h5 className="fw-bold">{contact.name}</h5>
                    <p className="text-muted mb-1">Role: {contact.role}</p>
                    <p className="mb-1">
                      Email:{' '}
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-primary"
                      >
                        {contact.email}
                      </a>
                    </p>
                    <p>
                      Phone:{' '}
                      <a href={`tel:${contact.phone}`} className="text-primary">
                        {contact.phone}
                      </a>
                    </p>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Alert variant="warning" className="mt-3">
            No Contacts Found
          </Alert>
        )}
      </div>
      <div className="mt-5">
        <h3 className="fw-bold"> Address Details</h3>
        {institute?.address && institute.address.length > 0 ? (
          <div className="row g-4">
            {institute.address.map(address => (
              <div key={address.address_id} className="col-md-6 col-lg-4">
                <Card className="border-0 shadow-sm p-3">
                  <Card.Body>
                    <h5 className="fw-bold">{address.address_type}</h5>
                    <p className="text-muted mb-1">{address.street}</p>
                    <p className="mb-1">
                      {address.city}, {address.state} {address.zip}
                    </p>
                    <p className="text-muted">CDC Flag: {address.CDC_FLAG}</p>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Alert variant="warning" className="mt-3">
            No Address Found
          </Alert>
        )}
      </div>

      {/* Back Button */}
      <div className="mt-4">
        <Button
          variant="outline-secondary"
          onClick={() => window.history.back()}
          className="d-flex align-items-center"
        >
          {/* <ArrowLeft size={18} className="me-2" /> */}
          Back
        </Button>
      </div>
    </div>
  );
};

export default InstituteDetails;
